'use client'

import React, { useState, useEffect } from 'react'
import HomePanelWrapper from '@/src/components/commons/generics/HomePanelWrapper/HomePanelWrapper'
import GenericCTA from '../GenericCTA/GenericCTA'
import { PromotionalBladeProps } from './PromotionalBlade.interface'
import styles from './PromotionalBlade.module.scss'

const PromotionalBlade: React.FC<PromotionalBladeProps> = ({
  eyebrow,
  title,
  textLong,
  ctaLink,
  backgroundImage,
  mobileBackgroundImage,
  tabletBackgroundImage,
  className, // <-- This is the prop we need to use
}) => {
  // State to hold the currently selected image URL (starts with the desktop default/mandatory image)
  const [currentSrc, setCurrentSrc] = useState(backgroundImage.url)

  // 💡 useEffect to determine the source URL based on screen size
  useEffect(() => {
    // Skip execution on the server
    if (typeof window === 'undefined') return

    const determineSource = () => {
      const width = window.innerWidth
      let newSrc = backgroundImage.url // Default to desktop (mandatory)

      // Mobile breakpoint check (width < 768px)
      if (width < 768 && mobileBackgroundImage?.url) {
        newSrc = mobileBackgroundImage.url
      }
      // Tablet breakpoint check (768px <= width < 1024px)
      else if (width < 1024 && tabletBackgroundImage?.url) {
        newSrc = tabletBackgroundImage.url
      }
      // Otherwise, use Desktop (mandatory/default)

      // Only update state if the source URL has actually changed
      if (newSrc !== currentSrc) {
        setCurrentSrc(newSrc)
      }
    }

    // Run once on mount
    determineSource()
    // then add event listener for dynamic resizing
    window.addEventListener('resize', determineSource)

    // Cleanup the event listener when the component unmounts
    return () => window.removeEventListener('resize', determineSource)
  }, [mobileBackgroundImage, tabletBackgroundImage, backgroundImage, currentSrc])

  return (
    <HomePanelWrapper
      backgroundImageUrl={backgroundImage.url}
      mobileBackgroundImageUrl={mobileBackgroundImage?.url}
      tabletBackgroundImageUrl={tabletBackgroundImage?.url}
      altText={backgroundImage.alt || 'Promotional content background'}
      outerClassName={`${styles.promotionalBladeSection} ${className || ''}`}
      innerClassName={styles.promotionalBladeContainer}
    >
      {/* Content Overlay */}
      <div className={`${styles.contentOverlay} w-full m-0`}>
        <div className={`${styles.contentWrapper} !text-left font-gt-haptik w-full m-0 relative`}>
          {eyebrow && (
            <p
              className={`${styles.eyebrow} font-dm-mono mb-[20px] text-[14px] font-medium flex items-center`}
            >
              {eyebrow}
            </p>
          )}
          <h2 className="text-[26px] md:text-[30px] lg:text-[36px] leading-[30px] tracking-[-0.26px] md:tracking-[-0.3px] mb-[10px] font-medium">
            {title}
          </h2>
          <p className={`${styles.textLong} text-[16px] md:text-[20px] lg:text-[24px] font-normal`}>
            {textLong}
          </p>
          <div className={styles.ctaContainer}>
            <GenericCTA
              ctaText={ctaLink.ctaText}
              ctaURL={ctaLink.ctaURL}
              variant="red"
              className={styles.promoBladeCTA}
            />
          </div>
        </div>
      </div>
    </HomePanelWrapper>
  )
}

export default PromotionalBlade
